import { defineModule } from '@nexus/core';
import {
  inspectionCreated,
  inspectionDeleted,
  inspectionStatusChanged,
  inspectionUpdated,
} from './events';

const READ = 'sifa.inspection.read';
const WRITE = 'sifa.inspection.write';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'sifa',
  name: 'sifa.name',
  description: 'sifa.description',
  icon: 'shield-check',
  theme: { accent: 'green' },
  // The care of a client is a project, its sites are objects of projects; clients and operators
  // come from crm, norms for the legal basis of findings and their AI drafts from legal.
  dependsOn: ['projects', 'crm', 'legal'],
  permissions: [
    // Scope "own": inspections the person inspects or created.
    { key: READ, scopes: ['own', 'all'] },
    { key: WRITE, scopes: ['own', 'all'] },
    { key: 'sifa.inspection.delete', privileged: true },
    { key: 'sifa.inspection.comment' },
    { key: 'sifa.inspection.export' },
    // Live search for substances in GESTIS; nothing is stored.
    { key: 'sifa.substance.search' },
    // Catalogs, report templates, questionnaires and the settings.
    { key: 'sifa.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'inspector',
      name: 'sifa.roles.inspector',
      grants: [
        { permission: READ, scope: 'all' },
        { permission: WRITE, scope: 'own' },
        { permission: 'sifa.inspection.comment', scope: null },
        { permission: 'sifa.inspection.export', scope: null },
        { permission: 'sifa.substance.search', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'sifa.roles.manager',
      grants: [
        // Unscoped permissions of the wildcard ignore the scope.
        { permission: 'sifa.inspection.*', scope: 'all' },
        { permission: 'sifa.substance.search', scope: null },
        { permission: 'sifa.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'sifa.nav.inspections',
      path: '/sifa/inspections',
      icon: 'shield-check',
      permission: READ,
    },
    {
      label: 'sifa.nav.substances',
      path: '/sifa/substances',
      icon: 'file-text',
      permission: 'sifa.substance.search',
    },
  ],
  // The care of a client is a project, the site an object: on the project page first, on the
  // object page after fire safety and SiGeKo.
  recordSections: [
    {
      key: 'sifa.project-inspections',
      target: 'projects.project',
      title: 'sifa.sections.inspections',
      permission: READ,
      order: 20,
    },
    {
      key: 'sifa.object-inspections',
      target: 'projects.object',
      title: 'sifa.sections.inspections',
      permission: READ,
      order: 30,
    },
  ],
  search: [{ entity: 'sifa.inspection', permission: READ }],
  files: [
    {
      entity: 'sifa.inspection',
      read: READ,
      write: WRITE,
      kinds: ['pdf', 'image'],
      maxBytes: 25_000_000,
    },
  ],
  activity: [
    {
      entity: 'sifa.inspection',
      read: READ,
      comment: 'sifa.inspection.comment',
      path: '/sifa/inspections/$id',
    },
  ],
  views: [{ list: 'sifa.inspections', read: READ, share: 'sifa.settings.manage' }],
  findings: [{ entity: 'sifa.inspection', read: READ, write: WRITE }],
  documents: [
    {
      key: 'sifa.inspection-report',
      entity: 'sifa.inspection',
      label: 'sifa.documents.inspectionReport',
      permission: READ,
      placeholders: [
        ...placeholders('project', ['number', 'title']),
        ...placeholders('object', [
          'name',
          'number',
          'customerNumber',
          'address',
          'street',
          'postalCode',
          'city',
          'state',
          'usage',
          'buildingClass',
        ]),
        // Client of the project, else operator, else owner of the object.
        ...placeholders('customer', [
          'name',
          'number',
          'address',
          'phone',
          'email',
          'website',
          'contact',
        ]),
        ...placeholders('inspection', [
          'title',
          'date',
          'inspectors',
          'participants',
          'attendees',
          'status',
          'remarks',
        ]),
        ...placeholders('findings', ['count', 'open']),
      ],
      sources: [
        { key: 'attendees', kind: 'table', label: 'sifa.documents.sources.attendees' },
        { key: 'remarks', kind: 'text', label: 'sifa.documents.sources.remarks' },
      ],
    },
  ],
  questionnaires: [
    {
      key: 'sifa.general',
      entity: 'sifa.inspection',
      label: 'sifa.questionnaires.general',
      document: 'sifa.inspection-report',
    },
  ],
  offline: [
    {
      entity: 'sifa.inspection',
      label: 'sifa.offline.inspections',
      mode: 'read-write',
      // Only prepared inspections: one prepared itself, or those of a prepared project or object
      // (InspectionOffline).
      scope: 'prepared',
      conflict: 'field-merge',
      maxAge: 'P30D',
      permissions: { read: READ, write: WRITE },
      pins: [
        { entity: 'projects.project', field: 'projectId' },
        { entity: 'projects.object', field: 'objectId' },
        // An inspection prepared itself, with the plans of its project and object.
        { entity: 'sifa.inspection', field: 'id' },
      ],
    },
  ],
  ai: [
    {
      // Drafts a finding from notes taken on site. Notes describe the workplace, not people; email
      // addresses, phone numbers and IBANs are replaced anyway.
      key: 'sifa.finding-draft',
      label: 'sifa.ai.findingDraft',
      data: 'none',
      entities: ['sifa.inspection'],
      permission: WRITE,
      // One finding from bullet points with a fixed answer shape.
      model: 'small',
    },
  ],
  events: {
    publishes: [
      inspectionCreated.type,
      inspectionUpdated.type,
      inspectionStatusChanged.type,
      inspectionDeleted.type,
    ],
  },
  dataProcessing: [
    {
      entity: 'sifa.inspection',
      purpose:
        'Nachweis der Begehungen der Fachkraft für Arbeitssicherheit (§ 6 ASiG, DGUV Vorschrift 2) gegenüber Auftraggeber, Unfallversicherungsträger und Behörden: Begeher, Anwesende und Teilnehmer, Bemerkungen, Allgemeine Angaben, Feststellungen mit Fotos und Verantwortlichen und Anhänge; KI-Mangelerfassung aus Stichpunkten',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // Inspectors are account IDs; attendees and participants are names and functions of third
      // parties; remarks, answers and findings are free text; photos can show people and keep the
      // GPS position where they were taken.
      categories: ['account-id', 'name', 'job-title', 'free-text', 'photos', 'location'],
      // Proposal: ten years after the report was sent (liability, records), inspections never
      // completed ten years after their date. Still to be confirmed with the DPO (open question 55).
      retention: { period: 'P10Y', after: 'completed' },
      erase: 'anonymize',
      // Notes for the AI draft of a finding go to the AI provider the company picked.
      processors: ['ai-provider'],
    },
  ],
});

/** Placeholders of a group of the report, with labels and group in the module texts. */
function placeholders(group: string, names: readonly string[]) {
  return names.map((name) => ({
    key: `${group}.${name}`,
    label: `sifa.documents.placeholders.${group}.${name}`,
    group: `sifa.documents.groups.${group}`,
  }));
}
